/**
 * The centre workspace: visual verification.
 *
 * Engineering geometry shown here is consumed from backend contracts. The
 * workspace never reconstructs wind-zone boundaries or engineering dimensions
 * from display values.
 */

import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { geometryService } from '../api/client';
import { EmptyState, MessageList, TabBar, type TabDefinition } from '../shared/components';
import { DEFAULT_DRAWING_DISPLAY_SETTINGS, type DrawingDisplaySettings } from './view/drawingDisplaySettings';
import { formatQuantityWithUnit } from '../shared/format';
import { AllRoofsOverview } from './all-roofs/AllRoofsOverview';
import { ProjectViewer3D } from './project-viewer/ProjectViewer3D';
import { RoofPlanEditor } from './roof-plan-editor/RoofPlanEditor';
import { fromContract, type GeometryModel } from './roof-plan-editor/model';
import type { LayerVisibility } from './roof-plan-editor/render';
import { ProfessionalSectionEditor } from './section-editor/ProfessionalSectionEditor';
import './section-editor/ProfessionalSectionEditor.css';
import { SiteLayoutEditor } from './site-layout/SiteLayoutEditor';
import { CalculatedFixingPlan } from './wind-zone-plan/CalculatedFixingPlan';
import { MembraneLayingPlan } from './wind-zone-plan/MembraneLayingPlan';
import { WindCalculationSummary } from './wind-zone-plan/WindCalculationSummary';
import { WindZoneInspector } from './wind-zone-plan/WindZoneInspector';
import { WindZonePlan } from './wind-zone-plan/WindZonePlan';
import { ZoneQuantityBreakdown } from './wind-zone-plan/ZoneQuantityBreakdown';
import { selectAuthoritativeDimensions, selectAuthoritativeZones } from './wind-zone-plan/authoritativeVisualState';
import './wind-zone-plan/WindZonePlan.css';
import './multi-roof/MultiRoofWorkspace.css';
import type { CalculationResponse, ModuleResult, Project, RoofModule, ValidationMessage } from '../gen/ejot/roofing/v1/types';

export type VisualWorkspaceTab = 'viewer' | 'wind-plan' | 'fixing-layout' | 'membrane-plan' | 'site' | 'plan' | 'section' | 'details' | 'messages';
export type VisualWorkspaceScope = 'site' | 'all' | 'roof';

const ROOF_TABS: readonly TabDefinition[] = [
  { id: 'viewer', label: '3D View' },
  { id: 'plan', label: 'Roof Geometry' },
  { id: 'membrane-plan', label: 'Membrane Plan' },
  { id: 'fixing-layout', label: 'Fixing Plan' },
  { id: 'section', label: 'Section / Profile' },
  { id: 'wind-plan', label: '2D Zones' },
  { id: 'details', label: 'Calculation Details' },
  { id: 'messages', label: 'Messages' },
];

const ALL_ROOFS_TABS: readonly TabDefinition[] = [
  { id: 'viewer', label: '3D View' },
  { id: 'plan', label: '2D View' },
  { id: 'wind-plan', label: '2D Zones' },
];

const SITE_TABS: readonly TabDefinition[] = [
  { id: 'site', label: '2D View' },
  { id: 'viewer', label: '3D View' },
];

export function visualTabsForScope(scope: VisualWorkspaceScope): readonly TabDefinition[] {
  if (scope === 'site') return SITE_TABS;
  if (scope === 'all') return ALL_ROOFS_TABS;
  return ROOF_TABS;
}

/**
 * The tab a number key selects, counted over the tabs the current scope
 * actually shows rather than a fixed list.
 *
 * Alt rather than Ctrl or Cmd: those already switch browser tabs, and taking
 * them over would make the application fight the window around it. Returns
 * undefined for anything that is not one of the workspace's own shortcuts.
 */
export function tabForShortcut(
  event: { key: string; altKey: boolean; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean },
  scope: VisualWorkspaceScope,
): VisualWorkspaceTab | undefined {
  if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return undefined;
  const position = Number(event.key);
  const tabs = visualTabsForScope(scope);
  if (!Number.isInteger(position) || position < 1 || position > tabs.length) return undefined;
  return tabs[position - 1]?.id as VisualWorkspaceTab | undefined;
}

/**
 * Counts what the Messages tab is holding, so a blocking error is visible
 * from the tab strip rather than only to whoever thinks to open it.
 *
 * Errors outrank warnings: when both are present the badge reports the
 * errors, because that is the count that decides whether the result stands.
 */
export function messagesTabBadge(messages: readonly ValidationMessage[]): Pick<
  TabDefinition, 'badge' | 'badgeTone' | 'badgeDescription'
> {
  let errors = 0;
  let warnings = 0;
  for (const message of messages) {
    if (message.severity === 'SEVERITY_ERROR') errors += 1;
    else if (message.severity === 'SEVERITY_WARNING') warnings += 1;
  }
  if (errors > 0) {
    return {
      badge: errors,
      badgeTone: 'error',
      badgeDescription: `${errors} ${errors === 1 ? 'error' : 'errors'}`,
    };
  }
  if (warnings > 0) {
    return {
      badge: warnings,
      badgeTone: 'warning',
      badgeDescription: `${warnings} ${warnings === 1 ? 'warning' : 'warnings'}`,
    };
  }
  return {};
}

export function CentreWorkspace({
  project,
  result,
  messages,
  visualStateStale,
  layers,
  drawingDisplaySettings = DEFAULT_DRAWING_DISPLAY_SETTINGS,
  geometry,
  theme,
  scope,
  activeTab,
  activeModuleId,
  selectedZoneId,
  onScopeChange,
  onTabChange,
  onActiveModuleChange,
  onOpenModule,
  onProjectChange,
  onZoneSelect,
  onGeometryChange,
  onLayerToggle,
}: {
  project: Project;
  result: CalculationResponse | undefined;
  messages: readonly ValidationMessage[];
  visualStateStale?: boolean;
  layers: LayerVisibility;
  drawingDisplaySettings?: DrawingDisplaySettings;
  geometry: GeometryModel;
  roofHeightMillimetres: number;
  theme: string;
  scope: VisualWorkspaceScope;
  activeTab: VisualWorkspaceTab;
  activeModuleId: string | undefined;
  selectedZoneId: string | undefined;
  onScopeChange: (scope: VisualWorkspaceScope) => void;
  onTabChange: (tab: VisualWorkspaceTab) => void;
  onActiveModuleChange: (moduleId: string) => void;
  onOpenModule: (moduleId: string, preferredTab?: VisualWorkspaceTab) => void;
  onProjectChange: (project: Project) => void;
  onZoneSelect: (zoneId: string | undefined) => void;
  onGeometryChange: (model: GeometryModel) => void;
  onLayerToggle?: (layerId: string, visible: boolean) => void;
}) {
  const moduleId = activeModuleId || project.modules?.[0]?.id || '';
  const activeProjectModule = project.modules?.find((module) => module.id === moduleId) ?? project.modules?.[0];
  const activeGeometry = useMemo(
    () => activeProjectModule?.build_up?.plan ? fromContract(activeProjectModule.build_up.plan) : geometry,
    [activeProjectModule?.build_up?.plan, geometry],
  );
  const secondaryTabs = visualTabsForScope(scope);

  useEffect(() => {
    if (!secondaryTabs.some((tab) => tab.id === activeTab)) {
      onTabChange((scope === 'site' ? 'site' : 'viewer') as VisualWorkspaceTab);
    }
  }, [activeTab, onTabChange, scope, secondaryTabs]);

  const zonePreview = useQuery({
    queryKey: ['wind-zone-preview', project, moduleId],
    queryFn: ({ signal }) => geometryService.generateWindZones({
      project,
      module_id: moduleId,
      request_token: `${project.id || 'project'}:${moduleId}:${activeProjectModule?.build_up?.plan?.revision ?? 0}`,
    }, signal),
    enabled: scope === 'roof' && Boolean(moduleId && project.design_method_id),
    retry: false,
  });

  const moduleResult = useMemo(() => (
    result?.modules?.find((module) => module.module_id === moduleId) ?? result?.modules?.[0]
  ), [result, moduleId]);
  const calculatedZones = moduleResult?.zones ?? [];
  const zones = useMemo(
    () => selectAuthoritativeZones(moduleResult?.geometry?.wind_zones, zonePreview.data?.zones),
    [moduleResult?.geometry?.wind_zones, zonePreview.data?.zones],
  );
  const dimensions = useMemo(
    () => selectAuthoritativeDimensions(moduleResult?.geometry?.dimensions, zonePreview.data?.dimensions),
    [moduleResult?.geometry?.dimensions, zonePreview.data?.dimensions],
  );
  const fixingScene = useMemo(
    () => moduleResult?.drawing_scenes?.find((scene) => scene.id === 'fixing-plan'),
    [moduleResult?.drawing_scenes],
  );
  const membraneScene = useMemo(
    () => moduleResult?.drawing_scenes?.find((scene) => scene.id === 'membrane-layout'),
    [moduleResult?.drawing_scenes],
  );

  useEffect(() => {
    if (selectedZoneId && !zones.some((zone) => zone.id === selectedZoneId)) {
      onZoneSelect(undefined);
    }
  }, [zones, selectedZoneId, onZoneSelect]);

  const combinedMessages = useMemo(
    () => [...messages, ...(zonePreview.data?.messages ?? [])],
    [messages, zonePreview.data?.messages],
  );
  const fixingMessages = useMemo(
    () => [...(result?.messages ?? []), ...(moduleResult?.messages ?? [])],
    [result?.messages, moduleResult?.messages],
  );
  const badgedTabs = useMemo<readonly TabDefinition[]>(
    () => secondaryTabs.map((tab) => (tab.id === 'messages'
      ? { ...tab, ...messagesTabBadge(combinedMessages) }
      : tab)),
    [combinedMessages, secondaryTabs],
  );

  const selectScope = (target: string) => {
    if (target === 'site') {
      onScopeChange('site');
      onTabChange('site');
      return;
    }
    if (target === 'all') {
      onScopeChange('all');
      onTabChange('viewer');
      return;
    }
    if (target.startsWith('roof:')) {
      const id = target.slice('roof:'.length);
      if (id) onActiveModuleChange(id);
      onScopeChange('roof');
      if (!ROOF_TABS.some((tab) => tab.id === activeTab)) onTabChange('viewer');
    }
  };

  return (
    <section className="panel centre-panel">
      <header className="panel-header">
        <span>Visual verification</span>
      </header>

      <VisualScopeTabs
        project={project}
        scope={scope}
        activeModuleId={moduleId}
        onSelect={selectScope}
      />

      <div className="visual-secondary-navigation">
        <TabBar
          tabs={badgedTabs}
          active={activeTab}
          onSelect={(id) => onTabChange(id as VisualWorkspaceTab)}
          ariaLabel="Views available in the selected project or roof scope"
        />
      </div>

      <div className="panel-content centre-content" id={`panel-${activeTab}`} role="tabpanel" aria-labelledby={`tab-${activeTab}`}>
        {scope === 'site' && activeTab === 'site' && (
          <SiteLayoutEditor
            project={project}
            layers={layers}
            activeModuleId={moduleId}
            onActiveModuleChange={onActiveModuleChange}
            onOpenModule={(id) => onOpenModule(id, 'plan')}
            onProjectChange={onProjectChange}
          />
        )}

        {scope === 'site' && activeTab === 'viewer' && (
          <AllRoofsOverview
            project={project}
            result={result}
            mode="3d"
            activeModuleId={moduleId}
            layers={layers}
            theme={theme}
            selectedZoneId={selectedZoneId}
            onZoneSelect={onZoneSelect}
            onActiveModuleChange={onActiveModuleChange}
            onOpenModule={(id) => onOpenModule(id, 'viewer')}
          />
        )}

        {scope === 'all' && activeTab === 'viewer' && (
          <AllRoofsOverview
            project={project}
            result={result}
            mode="3d"
            activeModuleId={moduleId}
            layers={layers}
            theme={theme}
            selectedZoneId={selectedZoneId}
            onZoneSelect={onZoneSelect}
            onActiveModuleChange={onActiveModuleChange}
            onOpenModule={(id) => onOpenModule(id, 'viewer')}
          />
        )}

        {scope === 'all' && activeTab === 'plan' && (
          <AllRoofsOverview
            project={project}
            result={result}
            mode="2d"
            activeModuleId={moduleId}
            onActiveModuleChange={onActiveModuleChange}
            onOpenModule={(id) => onOpenModule(id, 'plan')}
          />
        )}

        {scope === 'all' && activeTab === 'wind-plan' && (
          <AllRoofsOverview
            project={project}
            result={result}
            mode="zones"
            activeModuleId={moduleId}
            onActiveModuleChange={onActiveModuleChange}
            onOpenModule={(id) => onOpenModule(id, 'wind-plan')}
          />
        )}

        {scope === 'roof' && activeTab === 'viewer' && (
          <ProjectViewer3D
            project={project}
            result={result}
            activeModuleId={moduleId}
            detailMode="active"
            layers={layers}
            theme={theme}
            selectedZoneId={selectedZoneId}
            onZoneSelect={onZoneSelect}
            onActiveModuleChange={onActiveModuleChange}
          />
        )}

        {scope === 'roof' && activeTab === 'wind-plan' && (
          <div className="wind-zone-plan-layout">
            <WindZonePlan
              zones={zones}
              dimensions={dimensions}
              results={calculatedZones}
              messages={combinedMessages}
              layers={layers}
              selectedZoneId={selectedZoneId}
              onZoneSelect={(zoneId) => onZoneSelect(zoneId)}
            />
            <WindZoneInspector
              zones={zones}
              results={calculatedZones}
              selectedZoneId={selectedZoneId}
              onSelect={onZoneSelect}
            />
          </div>
        )}

        {scope === 'roof' && activeTab === 'fixing-layout' && (
          <CalculatedFixingPlan
            exportFileName={`${orModuleName(activeProjectModule, moduleId)}-fixing-layout`}
            scene={fixingScene}
            zones={zones}
            results={calculatedZones}
            messages={fixingMessages}
            layers={layers}
            displaySettings={drawingDisplaySettings}
            selectedZoneId={selectedZoneId}
            onZoneSelect={onZoneSelect}
            onLayerToggle={onLayerToggle}
          />
        )}

        {scope === 'roof' && activeTab === 'membrane-plan' && (
          <MembraneLayingPlan
            exportFileName={`${orModuleName(activeProjectModule, moduleId)}-membrane-layout`}
            scene={membraneScene}
            zones={zones}
            results={calculatedZones}
            messages={fixingMessages}
            layers={layers}
            displaySettings={drawingDisplaySettings}
            selectedZoneId={selectedZoneId}
            onZoneSelect={onZoneSelect}
            membraneTakeoff={moduleResult?.membrane_takeoff}
            onLayerToggle={onLayerToggle}
          />
        )}

        {scope === 'roof' && activeTab === 'plan' && (
          <RoofPlanEditor
            key={`${moduleId}:${activeProjectModule?.build_up?.plan?.revision ?? 0}`}
            layers={layers}
            geometry={activeGeometry}
            onGeometryChange={onGeometryChange}
          />
        )}

        {scope === 'roof' && activeTab === 'section' && <ProfessionalSectionEditor module={activeProjectModule} />}
        {scope === 'roof' && activeTab === 'details' && <CalculationDetails result={result} module={moduleResult} />}
        {scope === 'roof' && activeTab === 'messages' && (
          <div className="centre-scroll">
            <MessageList messages={combinedMessages} emptyText="No messages." />
          </div>
        )}
      </div>

      {scope === 'roof' && (visualStateStale ? (
        <div className="centre-preview-status" role="status" aria-live="polite">
          Current inputs are not calculable — showing the last valid visualization. 2D, 3D and editors remain available.
        </div>
      ) : zonePreview.isFetching ? (
        <div className="centre-preview-status" role="status">
          Updating authoritative wind-zone preview…
        </div>
      ) : null)}
    </section>
  );
}

function VisualScopeTabs({ project, scope, activeModuleId, onSelect }: {
  project: Project;
  scope: VisualWorkspaceScope;
  activeModuleId: string;
  onSelect: (target: string) => void;
}) {
  const active = scope === 'site' ? 'site' : scope === 'all' ? 'all' : `roof:${activeModuleId}`;
  return (
    <div className="visual-scope-tabs" role="tablist" aria-label="Project and roof workspace hierarchy">
      <button
        type="button"
        role="tab"
        aria-selected={active === 'site'}
        className={active === 'site' ? 'visual-scope-tab is-active is-project-scope' : 'visual-scope-tab is-project-scope'}
        onClick={() => onSelect('site')}
      >
        Site Layout
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={active === 'all'}
        className={active === 'all' ? 'visual-scope-tab is-active is-project-scope' : 'visual-scope-tab is-project-scope'}
        onClick={() => onSelect('all')}
      >
        All Roofs
      </button>
      {(project.modules ?? []).map((module, index) => {
        const id = module.id ?? `roof-${index + 1}`;
        const target = `roof:${id}`;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={active === target}
            className={active === target ? 'visual-scope-tab is-active' : 'visual-scope-tab'}
            onClick={() => onSelect(target)}
          >
            {module.display_name?.trim() || `Roof ${index + 1}`}
          </button>
        );
      })}
    </div>
  );
}

function CalculationDetails({ result, module }: {
  result: CalculationResponse | undefined;
  module: ModuleResult | undefined;
}) {
  const trace = module?.trace;
  const assumptions = module?.assumptions ?? [];

  return (
    <div className="centre-scroll">
      <WindCalculationSummary module={module} />
      <ZoneQuantityBreakdown result={result} module={module} />

      {assumptions.length > 0 && (
        <section className="result-section">
          <h3 className="result-section-title">Assumptions</h3>
          <ul className="assumption-list">
            {assumptions.map((assumption) => (
              <li key={assumption.code}>
                <strong>{assumption.title}</strong>
                <p>{assumption.explanation}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="result-section">
        <h3 className="result-section-title">Calculation trace</h3>
        {!trace || (trace.entries ?? []).length === 0 ? (
          <EmptyState>
            No trace is available. A trace is produced by the calculation engine, which
            is delivered with the engineering phase.
          </EmptyState>
        ) : (
          <table className="engineering-table">
            <thead>
              <tr>
                <th scope="col">Step</th>
                <th scope="col">Rule</th>
                <th scope="col" className="numeric">Result</th>
              </tr>
            </thead>
            <tbody>
              {trace.entries?.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.description}</td>
                  <td><code>{entry.rule_id}</code></td>
                  <td className="numeric">{formatQuantityWithUnit(entry.result)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {result?.input_hash && (
        <section className="result-section">
          <h3 className="result-section-title">Result identity</h3>
          <p className="dialog-hash">{result.input_hash}</p>
        </section>
      )}
    </div>
  );
}

function orModuleName(module: RoofModule | undefined, moduleId: string | undefined): string {
  return module?.display_name?.trim() || module?.id || moduleId || 'module';
}
